// deps
import { useCallback, useContext, useMemo } from "react";

// internal-deps
import { UpiPaymentGroupActions, UpiSpecificActions } from "@swiggy-private/payment-ui";

// interfaces
import { PaymentGroupHandlerProps } from "@payment-client/components/show-payments/components/payment-group-handler/interfaces";

// hooks
import { useNavigate, CheckoutRoute } from "@swiggy-private/checkout-core";
import { usePaymentActions } from "./UsePaymentActions";

// contexts
import { UpiIntentContext, UpiIntentContextValue } from "@payment-client/contexts/UpiIntentContext";

interface UseUPIHandlerReturnType {
    availableUpiIntentMethods: string[];
    paymentActions: UpiPaymentGroupActions;
    upiSpecificActions: UpiSpecificActions;
    upiIntentMethodFetchInProgress: UpiIntentContextValue["upiIntentMethodFetchInProgress"];
}

interface UseUPIHandlerProps {
    onPay: PaymentGroupHandlerProps["onPay"];
    onSelect: PaymentGroupHandlerProps["onSelect"];
}

export const useUPIHandler = (props: UseUPIHandlerProps): UseUPIHandlerReturnType => {
    const actions = usePaymentActions({
        onPay: props.onPay,
        onSelect: props.onSelect,
    });
    const navigateTo = useNavigate();
    const { availableMethods: availableUpiIntentMethods, upiIntentMethodFetchInProgress } = useContext(
        UpiIntentContext,
    );

    const onSelectAddNewVpa = useCallback(
        (paymentMethodCode: string) => navigateTo(CheckoutRoute.VERIFY_VPA, { paymentMethodCode }),
        [navigateTo],
    );

    const upiSpecificActions = useMemo(
        (): UpiSpecificActions => ({
            onSelectAddNewVpa,
        }),
        [onSelectAddNewVpa],
    );

    const paymentActions = useMemo(
        (): UpiPaymentGroupActions => ({
            ...actions,
            ...upiSpecificActions,
        }),
        [actions, upiSpecificActions],
    );

    return { availableUpiIntentMethods, paymentActions, upiSpecificActions, upiIntentMethodFetchInProgress };
};
